import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  deleteHourOverride,
  listLocationHours,
  listLocationOverrides,
  upsertHourOverride,
  upsertWeeklyHours,
} from '@/lib/locations';
import { useLocations } from '@/stores/locations';
import { notify } from '@/lib/notify';
import {
  CrossOrgReadOnlyBanner,
  useIsReadOnlyCrossOrg,
} from '@/components/CrossOrgReadOnlyBanner';
import { DAYS_OF_WEEK, type DayOfWeek, type LocationHourOverride, type LocationHours as LocationHoursRow } from '@/lib/types';

/**
 * Hours editor: weekly pattern + date-specific overrides.
 *
 * Two stacked panels:
 *  1) Weekly hours — a 7-row grid (one per weekday). Each row has an Open?
 *     toggle and (open, close) time pickers when open.
 *  2) Hour overrides — list of (date, reason, status) rows. Add new override
 *     dialog at the top. Delete via per-row button.
 */
export default function LocationHours() {
  const { id } = useParams<{ id: string }>();
  const items = useLocations((s) => s.items);
  const status = useLocations((s) => s.status);
  const load = useLocations((s) => s.load);

  const loc = items.find((l) => l.id === id) ?? null;

  // Hook must be called every render — pass org id or null while loading.
  const isReadOnlyCrossOrg = useIsReadOnlyCrossOrg(loc?.organization_id);

  useEffect(() => {
    if (status === 'idle') void load();
  }, [status, load]);

  if (!id) return <div className="p-8 text-sm">Missing location id.</div>;
  if (status === 'idle' || status === 'loading')
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">
        Loading...
      </div>
    );
  if (!loc) {
    return (
      <div className="container mx-auto py-10 px-4 text-center space-y-2">
        <h1 className="text-xl font-semibold">Location not found</h1>
        <Link to="/locations" className="text-sm underline">
          Back to all locations
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <Link
          to={`/locations/${id}`}
          className="text-sm text-muted-foreground underline"
        >
          ← Back to {loc.name}
        </Link>

        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Hours</h1>
          <p className="text-sm text-muted-foreground">
            Weekly pattern is the default. Date-specific overrides take
            precedence on the dates they apply to.
          </p>
        </div>

        {isReadOnlyCrossOrg && (
          <CrossOrgReadOnlyBanner
            entityLabel="set of hours"
            blockedActionsLabel="edits to weekly hours and overrides"
          />
        )}

        {/* fieldset disabled cleanly disables every nested form control —
            inputs, selects, buttons — without us having to thread a
            readOnly prop through every panel. The banner above tells the
            user why. */}
        <fieldset
          disabled={isReadOnlyCrossOrg}
          className="space-y-6 disabled:opacity-90"
        >
          <WeeklyHoursPanel locationId={id} />
          <OverridesPanel locationId={id} />
        </fieldset>
      </div>
    </div>
  );
}

// ----- weekly hours panel --------------------------------------------------

interface WeekRow {
  day_of_week: DayOfWeek;
  is_closed: boolean;
  open_time: string; // HH:MM in form state
  close_time: string;
}

const DEFAULT_OPEN = '09:00';
const DEFAULT_CLOSE = '17:00';

function WeeklyHoursPanel({ locationId }: { locationId: string }) {
  const [rows, setRows] = useState<WeekRow[]>(() =>
    DAYS_OF_WEEK.map((d) => ({
      day_of_week: d.value,
      is_closed: false,
      open_time: DEFAULT_OPEN,
      close_time: DEFAULT_CLOSE,
    }))
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const dbRows = await listLocationHours(locationId);
        if (cancelled) return;
        setRows(mergeIntoWeek(dbRows));
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  function setRow(day: DayOfWeek, patch: Partial<WeekRow>) {
    setRows((current) =>
      current.map((r) => (r.day_of_week === day ? { ...r, ...patch } : r))
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    // Validate open < close on all open days
    for (const r of rows) {
      if (!r.is_closed) {
        if (!r.open_time || !r.close_time) {
          setError('Open days require both open and close times.');
          return;
        }
        if (r.close_time <= r.open_time) {
          setError(
            `On ${DAYS_OF_WEEK[r.day_of_week].label}, close time must be after open time.`
          );
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      await upsertWeeklyHours(
        locationId,
        rows.map((r) => ({
          day_of_week: r.day_of_week,
          is_closed: r.is_closed,
          open_time: r.is_closed ? null : `${r.open_time}:00`,
          close_time: r.is_closed ? null : `${r.close_time}:00`,
        }))
      );
      setSavedAt(new Date());
      notify.success('Hours saved');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading)
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading weekly hours...
      </div>
    );

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-border bg-card p-6 space-y-4"
    >
      <h2 className="text-base font-medium">Weekly hours</h2>

      <ul className="space-y-2">
        {rows.map((row) => {
          const day = DAYS_OF_WEEK[row.day_of_week];
          return (
            <li
              key={row.day_of_week}
              className="flex items-center gap-3 rounded-md border border-border p-3"
            >
              <div className="w-20 text-sm font-medium">{day.label}</div>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!row.is_closed}
                  onChange={(e) =>
                    setRow(row.day_of_week, { is_closed: !e.target.checked })
                  }
                  className="h-4 w-4 rounded border border-border"
                />
                Open
              </label>
              {!row.is_closed && (
                <div className="flex items-center gap-2 ml-auto">
                  <input
                    type="time"
                    value={row.open_time}
                    onChange={(e) =>
                      setRow(row.day_of_week, { open_time: e.target.value })
                    }
                    className={timeInputClass}
                  />
                  <span className="text-muted-foreground text-xs">to</span>
                  <input
                    type="time"
                    value={row.close_time}
                    onChange={(e) =>
                      setRow(row.day_of_week, { close_time: e.target.value })
                    }
                    className={timeInputClass}
                  />
                </div>
              )}
              {row.is_closed && (
                <span className="ml-auto text-xs text-muted-foreground">
                  Closed
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {error && (
        <p
          role="alert"
          className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}
      {savedAt && !error && (
        <p className="text-xs text-muted-foreground">
          Saved at {savedAt.toLocaleTimeString()}.
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save weekly hours'}
        </button>
      </div>
    </form>
  );
}

function mergeIntoWeek(dbRows: LocationHoursRow[]): WeekRow[] {
  return DAYS_OF_WEEK.map((d) => {
    const found = dbRows.find((r) => r.day_of_week === d.value);
    if (!found) {
      return {
        day_of_week: d.value,
        is_closed: false,
        open_time: DEFAULT_OPEN,
        close_time: DEFAULT_CLOSE,
      };
    }
    return {
      day_of_week: d.value,
      is_closed: found.is_closed,
      open_time: (found.open_time ?? `${DEFAULT_OPEN}:00`).slice(0, 5),
      close_time: (found.close_time ?? `${DEFAULT_CLOSE}:00`).slice(0, 5),
    };
  });
}

// ----- overrides panel ------------------------------------------------------

function OverridesPanel({ locationId }: { locationId: string }) {
  const [overrides, setOverrides] = useState<LocationHourOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New-override form state
  const [date, setDate] = useState('');
  const [isClosed, setIsClosed] = useState(false);
  const [openTime, setOpenTime] = useState(DEFAULT_OPEN);
  const [closeTime, setCloseTime] = useState(DEFAULT_CLOSE);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listLocationOverrides(locationId);
      setOverrides(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function onAdd(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!date) {
      setError('Pick a date for the override.');
      return;
    }
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    if (!isClosed && closeTime <= openTime) {
      setError('Close time must be after open time.');
      return;
    }

    setSubmitting(true);
    try {
      await upsertHourOverride(locationId, {
        override_date: date,
        is_closed: isClosed,
        open_time: isClosed ? null : `${openTime}:00`,
        close_time: isClosed ? null : `${closeTime}:00`,
        reason: reason.trim(),
      });
      setDate('');
      setIsClosed(false);
      setOpenTime(DEFAULT_OPEN);
      setCloseTime(DEFAULT_CLOSE);
      setReason('');
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(d: string) {
    if (!confirm(`Remove override for ${d}?`)) return;
    try {
      await deleteHourOverride(locationId, d);
      notify.info('Hour override removed');
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const sorted = useMemo(
    () => [...overrides].sort((a, b) => a.override_date.localeCompare(b.override_date)),
    [overrides]
  );

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-4">
      <div className="space-y-1">
        <h2 className="text-base font-medium">Date-specific overrides</h2>
        <p className="text-xs text-muted-foreground">
          For holidays, special hours, or one-off closures. Take precedence
          over the weekly pattern on the listed dates.
        </p>
      </div>

      <form
        onSubmit={onAdd}
        className="space-y-3 rounded-md border border-dashed border-border p-4"
      >
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Add an override
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="override-date">
              Date
            </label>
            <input
              id="override-date"
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="override-reason">
              Reason
            </label>
            <input
              id="override-reason"
              type="text"
              required
              maxLength={200}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Civic Holiday"
              className={inputClass}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isClosed}
            onChange={(e) => setIsClosed(e.target.checked)}
            className="h-4 w-4 rounded border border-border"
          />
          Closed all day
        </label>

        {!isClosed && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="override-open">
                Open
              </label>
              <input
                id="override-open"
                type="time"
                value={openTime}
                onChange={(e) => setOpenTime(e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="override-close">
                Close
              </label>
              <input
                id="override-close"
                type="time"
                value={closeTime}
                onChange={(e) => setCloseTime(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        )}

        {error && (
          <p
            role="alert"
            className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Adding...' : 'Add override'}
        </button>
      </form>

      <div className="space-y-2">
        {loading && (
          <p className="text-sm text-muted-foreground">Loading overrides...</p>
        )}
        {!loading && sorted.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No overrides yet. Add one above for a holiday or special day.
          </p>
        )}
        {!loading &&
          sorted.map((o) => (
            <div
              key={`${o.location_id}:${o.override_date}`}
              className="flex items-center gap-3 rounded-md border border-border p-3 text-sm"
            >
              <div className="w-28 font-medium">{o.override_date}</div>
              <div className="text-xs text-muted-foreground flex-1">
                {o.reason}
              </div>
              <div className="text-xs text-muted-foreground">
                {o.is_closed
                  ? 'Closed'
                  : `${(o.open_time ?? '').slice(0, 5)}–${(o.close_time ?? '').slice(0, 5)}`}
              </div>
              <button
                type="button"
                onClick={() => onDelete(o.override_date)}
                className="rounded-md border border-border px-2 py-0.5 text-xs hover:bg-muted/40"
              >
                Remove
              </button>
            </div>
          ))}
      </div>
    </section>
  );
}

// ----- shared styles -------------------------------------------------------

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const timeInputClass =
  'rounded-md border border-input bg-background px-2 py-1 text-sm';
